import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from './AppLayout';
import { getCookie } from './cookieHelper';
import { buttonStyle, cardTitle, sectionStyle, subtitle } from './quoteStyles';

const toSummaryFromApplication = application => ({
  applicationId: application.applicationId,
  productType: application.productType,
  status: application.status,
  customerName: application.customer?.fullName || '-',
  customerEmail: application.customer?.email || '-',
  consultantQuoteMonthly: application.consultantQuote?.monthly || null,
  consultantQuoteAnnual: application.consultantQuote?.annual || null,
  createdAt: application.createdAt || new Date().toISOString(),
  updatedAt: application.updatedAt || new Date().toISOString(),
});

const upsertSummary = (list, next) => {
  const index = list.findIndex(item => item.applicationId === next.applicationId);
  if (index === -1) return [next, ...list];
  const copy = [...list];
  copy[index] = { ...copy[index], ...next };
  return copy;
};

const ConsultantDashboard = () => {
  const navigate = useNavigate();
  const role = getCookie('role');
  const username = getCookie('username');

  const [applications, setApplications] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pageError, setPageError] = useState('');

  const canVerify = !busy && selected?.status === 'SUBMITTED';
  const canCalculate = !busy && (selected?.status === 'VERIFIED' || selected?.status === 'QUOTE_SENT');
  const canSend =
    !busy &&
    (selected?.status === 'VERIFIED' || selected?.status === 'QUOTE_SENT') &&
    Boolean(selected?.consultantQuote);

  const calculateHint = canCalculate ? '' : 'Verify application first.';
  const sendHint = canSend ? '' : 'Verify and calculate quote first.';

  const loadApplications = async () => {
    setPageError('');
    const response = await fetch('/api/consultant/applications', {
      credentials: 'include',
      headers: { token: getCookie('token') },
    });

    const data = await response.json().catch(() => null);
    if (!response.ok) {
      if (response.status === 403) {
        navigate('/consultant/login');
        return;
      }
      setPageError((data && data.error) || `Unable to load applications (${response.status})`);
      return;
    }

    setApplications(Array.isArray(data) ? data : []);
  };

  const openApplication = async applicationId => {
    setPageError('');
    const response = await fetch(`/api/consultant/application/${applicationId}`, {
      credentials: 'include',
      headers: { token: getCookie('token') },
    });

    const data = await response.json().catch(() => ({ error: 'Unexpected server response' }));
    if (!response.ok) {
      setPageError(data.error || 'Unable to load application');
      return;
    }

    setSelected(data);
    setApplications(prev => upsertSummary(prev, toSummaryFromApplication(data)));
  };

  const runAction = async (endpoint, body = {}) => {
    if (!selected) return;

    setBusy(true);
    setPageError('');
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          token: getCookie('token'),
        },
        body: JSON.stringify(body),
      });

      const data = await response.json().catch(() => ({ error: 'Unexpected server response' }));
      if (!response.ok) {
        setPageError(data.error || `Action failed (${response.status})`);
        return;
      }

      if (endpoint.includes('/send-quote-email')) {
        setSelected(prev =>
          prev
            ? {
                ...prev,
                status: data.status || 'QUOTE_SENT',
                emailDispatch: data.emailDispatch || prev.emailDispatch,
                updatedAt: new Date().toISOString(),
              }
            : prev
        );

        setApplications(prev =>
          selected
            ? upsertSummary(prev, {
                applicationId: selected.applicationId,
                status: data.status || 'QUOTE_SENT',
                updatedAt: new Date().toISOString(),
              })
            : prev
        );
        return;
      }

      if (data && data.applicationId) {
        setSelected(data);
        setApplications(prev => upsertSummary(prev, toSummaryFromApplication(data)));
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (role !== 'consultant') {
      navigate('/consultant/login');
      return;
    }
    loadApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, navigate]);

  return (
    <AppLayout role={role} username={username}>
      <h1 style={cardTitle}>Consultant Dashboard</h1>
      <p style={subtitle}>Review submitted applications, verify, and send quote by email.</p>
      {pageError && <div style={styles.errorBanner}>{pageError}</div>}

      <div style={{ marginBottom: 10 }}>
        <button style={styles.viewButton} onClick={loadApplications} disabled={busy}>
          Refresh Applications
        </button>
      </div>

      <div style={styles.layout}>
        <div style={sectionStyle}>
          <h3 style={styles.heading}>Applications</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {applications.length === 0 ? (
                <tr>
                  <td style={styles.td} colSpan={5}>No submitted applications yet.</td>
                </tr>
              ) : (
                applications.map(item => (
                  <tr key={item.applicationId}>
                    <td style={styles.td}>{item.applicationId}</td>
                    <td style={styles.td}>{item.customerName}</td>
                    <td style={styles.td}>{item.customerEmail}</td>
                    <td style={styles.td}>{item.status}</td>
                    <td style={styles.td}>
                      <button style={styles.viewButton} onClick={() => openApplication(item.applicationId)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={sectionStyle}>
          <h3 style={styles.heading}>Application Details</h3>

          {!selected ? (
            <div style={styles.empty}>Select an application to process.</div>
          ) : (
            <>
              <div style={styles.detail}>Application ID: <b>{selected.applicationId}</b></div>
              <div style={styles.detail}>Product: <b>{selected.productType}</b></div>
              <div style={styles.detail}>Customer: <b>{selected.customer?.fullName}</b></div>
              <div style={styles.detail}>Email: <b>{selected.customer?.email}</b></div>
              <div style={styles.detail}>Phone: <b>{selected.customer?.phone}</b></div>
              <div style={styles.detail}>Gender: <b>{selected.customer?.gender || '-'}</b></div>
              <div style={styles.detail}>Smoker: <b>{selected.customer?.smoker ? 'Yes' : 'No'}</b></div>
              <div style={styles.detail}>Occupation: <b>{selected.customer?.occupation || '-'}</b></div>
              <div style={styles.detail}>Sum insured: <b>${selected.coverage?.sumInsured}</b></div>
              <div style={styles.detail}>Payment: <b>{selected.coverage?.paymentFrequency}</b></div>
              <div style={styles.detail}>Status: <b>{selected.status}</b></div>
              <div style={styles.detail}>
                Quote: <b>{selected.consultantQuote ? `$${selected.consultantQuote.monthly}/month` : 'Not calculated'}</b>
              </div>
              <div style={styles.detail}>
                Email sent: <b>{selected.emailDispatch?.sentAt ? new Date(selected.emailDispatch.sentAt).toLocaleString() : 'Not sent'}</b>
              </div>

              <div style={styles.actions}>
                <button
                  style={{ ...buttonStyle, ...(canVerify ? {} : styles.disabledButton) }}
                  disabled={!canVerify}
                  onClick={() => runAction(`/api/consultant/application/${selected.applicationId}/verify`)}
                >
                  Verify Application
                </button>

                <button
                  style={{ ...buttonStyle, ...(canCalculate ? {} : styles.disabledButton) }}
                  disabled={!canCalculate}
                  onClick={() =>
                    runAction(`/api/consultant/application/${selected.applicationId}/calculate-quote`, {
                      notes: 'Verified and prepared after consultant review.',
                    })
                  }
                >
                  Calculate Quote
                </button>
                {!canCalculate && <div style={styles.hintText}>{calculateHint}</div>}

                <button
                  style={{
                    ...buttonStyle,
                    background: 'linear-gradient(90deg, #0f7890 0%, #1199b8 100%)',
                    ...(canSend ? {} : styles.disabledButton),
                  }}
                  disabled={!canSend}
                  onClick={() =>
                    runAction(`/api/consultant/application/${selected.applicationId}/send-quote-email`)
                  }
                >
                  Send Quote Email
                </button>
                {!canSend && <div style={styles.hintText}>{sendHint}</div>}
              </div>
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

const styles = {
  layout: {
    display: 'grid',
    gap: 16,
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  },
  heading: {
    color: '#236946',
    margin: '0 0 10px 0',
  },
  table: {
    borderCollapse: 'collapse',
    width: '100%',
  },
  th: {
    borderBottom: '1px solid #cfe2d6',
    color: '#236946',
    fontSize: 13,
    padding: '8px 7px',
    textAlign: 'left',
  },
  td: {
    borderBottom: '1px solid #e0ede5',
    color: '#335544',
    fontSize: 13,
    padding: '8px 7px',
  },
  viewButton: {
    background: '#f0f9f3',
    border: '1px solid #bdd8c7',
    borderRadius: 6,
    color: '#236946',
    cursor: 'pointer',
    fontWeight: 700,
    padding: '6px 10px',
  },
  empty: {
    color: '#6a7f72',
    fontSize: 14,
  },
  detail: {
    color: '#335544',
    marginBottom: 8,
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 12,
  },
  hintText: {
    alignSelf: 'center',
    color: '#6d7f74',
    fontSize: 12,
    marginRight: 8,
  },
  disabledButton: {
    cursor: 'not-allowed',
    filter: 'grayscale(0.3)',
    opacity: 0.55,
  },
  errorBanner: {
    background: '#fff5f5',
    border: '1px solid #f3b8b8',
    borderRadius: 8,
    color: '#9e2e2e',
    marginBottom: 12,
    padding: '10px 12px',
  },
};

export default ConsultantDashboard;
